import React, { useState } from 'react';
import { X, Copy, Check, ExternalLink, Download, Code2, FileText, Calendar, HardDrive, Cpu } from 'lucide-react';
import { ClipItem } from '../types';
import confetti from 'canvas-confetti';

interface ClipModalProps {
  clip: ClipItem | null;
  onClose: () => void;
  onCopy: (clip: ClipItem) => void;
}

export const ClipModal: React.FC<ClipModalProps> = ({ clip, onClose, onCopy }) => {
  const [copied, setCopied] = useState(false);

  if (!clip) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(clip.content);
    setCopied(true);
    onCopy(clip);
    try {
      confetti({
        particleCount: 25,
        spread: 50,
        origin: { y: 0.7 },
        colors: ['#00F0FF', '#7000FF', '#FFFFFF']
      });
    } catch {}
    setTimeout(() => setCopied(false), 2000);
  };

  const isCode = clip.metadata.type === 'code';
  const isImage = clip.metadata.type === 'image';
  const isFile = clip.metadata.type === 'file';
  const isUrl = clip.metadata.type === 'url';
  const isColor = clip.metadata.type === 'color';

  const charCount = clip.content.length;
  const wordCount = clip.content.trim().split(/\s+/).filter(Boolean).length;
  const lineCount = clip.content.split('\n').length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl animate-in fade-in duration-200">
      <div className="glass-card w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl border border-cyan-500/30 overflow-hidden relative">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10 bg-black/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-950/60 border border-cyan-500/40 flex items-center justify-center text-cyan-400 shadow-glow-cyan">
              {isCode ? <Code2 size={20} /> : <FileText size={20} />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white font-display capitalize">
                  {clip.metadata.type} Inspector
                </h3>
                <span className="text-[10px] font-mono font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 px-2 py-0.5 rounded-full uppercase">
                  {clip.metadata.type}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                <span>From: {clip.metadata.senderDeviceName}</span>
                <span>•</span>
                <span>{new Date(clip.receivedAt).toLocaleTimeString()}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className={`btn-primary text-xs py-2 px-4 shadow-lg ${
                copied ? 'bg-emerald-500 text-black' : ''
              }`}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              <span>{copied ? 'Copied to Clipboard!' : 'Copy Full Content'}</span>
            </button>

            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-5 md:p-6 overflow-y-auto flex-1 space-y-4">
          {isImage ? (
            <div className="flex flex-col items-center justify-center gap-4 bg-black/60 p-4 rounded-2xl border border-white/5">
              <img
                src={clip.content}
                alt="Full preview"
                className="max-h-[60vh] max-w-full rounded-xl object-contain shadow-2xl"
              />
              <a
                href={clip.content}
                download={clip.metadata.fileName || 'synced-image.png'}
                className="btn-secondary text-xs py-2 px-4"
              >
                <Download size={14} />
                <span>Download High-Resolution Image</span>
              </a>
            </div>
          ) : isColor ? (
            <div className="p-8 rounded-2xl bg-black/60 border border-white/10 text-center space-y-4">
              <div
                className="w-24 h-24 rounded-3xl mx-auto shadow-2xl border-2 border-white/20"
                style={{ backgroundColor: clip.content.trim() }}
              />
              <p className="font-mono text-2xl font-bold text-white tracking-wider">
                {clip.content.trim()}
              </p>
            </div>
          ) : isCode ? (
            <div className="rounded-2xl bg-[#06070B] border border-white/10 overflow-hidden shadow-inner">
              <div className="flex items-center justify-between px-4 py-2 bg-black/60 border-b border-white/5 text-xs text-slate-400 font-mono">
                <span>Code Block</span>
                <span>{lineCount} lines • {charCount} chars</span>
              </div>
              <pre className="p-4 font-mono text-xs md:text-sm text-cyan-200 overflow-x-auto leading-relaxed whitespace-pre">
                {clip.content}
              </pre>
            </div>
          ) : (
            <div className="p-4 rounded-2xl bg-[#06070B] border border-white/10 text-slate-200 text-xs md:text-sm whitespace-pre-wrap leading-relaxed">
              {clip.content}
            </div>
          )}

          {/* Metadata Footer Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-2">
            <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-center">
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Size</span>
              <span className="font-mono text-xs font-bold text-slate-200">{clip.metadata.sizeBytes} B</span>
            </div>
            <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-center">
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Characters</span>
              <span className="font-mono text-xs font-bold text-slate-200">{charCount}</span>
            </div>
            <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-center">
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Words</span>
              <span className="font-mono text-xs font-bold text-slate-200">{wordCount}</span>
            </div>
            <div className="p-3 rounded-xl bg-black/40 border border-white/5 text-center">
              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">Encryption</span>
              <span className="font-mono text-xs font-bold text-cyan-400">AES-256</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
